import { log } from '../logger/logger.js';
import { isExcludedFromCMP } from './exclusionList.js';
import { setCurrentFramework } from './getCurrentFramework.js';
import { isConsentOrPayCountry, getSupportSignUpPage, setIsConsentOrPay } from './isConsentOrPay.js';
import { mark } from './lib/mark.js';
import { sendConsentChoicesToOphan, constructBannerMessageId, sendMessageReadyToOphan } from './lib/ophan.js';
import { ACCOUNT_ID, ENDPOINT, SourcePointChoiceTypes, PROPERTY_HREF_SUBDOMAIN, PROPERTY_HREF_MAIN, PROPERTY_ID_AUSTRALIA, PROPERTY_ID_MAIN, PROPERTY_ID_SUBDOMAIN } from './lib/sourcepointConfig.js';
import { mergeVendorList } from './mergeUserConsent.js';
import { invokeCallbacks } from './onConsentChange.js';
import { stub } from './stub.js';
let resolveWillShowPrivacyMessage;
const willShowPrivacyMessage = new Promise((resolve) => {
    resolveWillShowPrivacyMessage = resolve;
});
const getPropertyHref = (framework, useNonAdvertisedList) => {
    if (framework == "aus") {
        return "https://au.theguardian.com";
    }
    if (framework == "usnat") {
        return "https://www.theguardian.com";
    }
    return useNonAdvertisedList ? PROPERTY_HREF_SUBDOMAIN : PROPERTY_HREF_MAIN;
};
const getPropertyId = (framework, useNonAdvertisedList) => {
    if (framework == "aus") {
        return PROPERTY_ID_AUSTRALIA;
    }
    if (framework == "usnat") {
        return PROPERTY_ID_MAIN;
    }
    return useNonAdvertisedList ? PROPERTY_ID_SUBDOMAIN : PROPERTY_ID_MAIN;
};
const hasConsentedToNonAdvertisedList = () => {
    const spUserConsentString = localStorage.getItem(`_sp_user_consent_${PROPERTY_ID_SUBDOMAIN}`);
    const userConsent = JSON.parse(spUserConsentString ?? "{}");
    return userConsent.gdpr?.consentStatus.hasConsentData ?? false;
};
const shouldMergeVendorList = (countryCode, useNonAdvertisedList) => {
    return isConsentOrPayCountry(countryCode) && useNonAdvertisedList && !hasConsentedToNonAdvertisedList();
};
const init = (framework, countryCode, isUserSignedIn, useNonAdvertisedList, pubData = {}) => {
    stub(framework);
    if (window._sp_) {
        throw new Error("Sourcepoint global (window._sp_) is already defined!");
    }
    setCurrentFramework(framework);
    if (!isConsentOrPayCountry(countryCode)) {
        useNonAdvertisedList = false;
    }
    setIsConsentOrPay(isConsentOrPayCountry(countryCode) && !useNonAdvertisedList);
    invokeCallbacks();
    let frameworkMessageType;
    switch (framework) {
        case "usnat":
            frameworkMessageType = "usnat";
            break;
        case "aus":
            frameworkMessageType = "ccpa";
            break;
        case "tcfv2":
        default:
            frameworkMessageType = "gdpr";
            break;
    }
    let messageId;
    const isInPropertyIdABTest = window.guardian?.config?.tests?.useSourcepointPropertyIdVariant === "variant";
    log("cmp", `framework: ${framework}`);
    log("cmp", `frameworkMessageType: ${frameworkMessageType}`);
    const pageSection = window.guardian?.config?.page?.section;
    window._sp_queue = [];
    window._sp_ = {
        config: {
            baseEndpoint: ENDPOINT,
            accountId: ACCOUNT_ID,
            propertyId: getPropertyId(framework, useNonAdvertisedList),
            propertyHref: getPropertyHref(framework, useNonAdvertisedList),
            joinHref: true,
            isSPA: true,
            targetingParams: {
                framework,
                excludePage: isExcludedFromCMP(pageSection)
            },
            pubData: { ...pubData, cmpInitTimeUtc: ( /* @__PURE__ */new Date()).getTime() },
            // ccpa or gdpr object added below
            events: {
                onConsentReady: (message_type, consentUUID, euconsent) => {
                    log("cmp", `onConsentReady ${message_type}`);
                    if (message_type != frameworkMessageType) {
                        return;
                    }
                    log("cmp", `consentUUID ${consentUUID}`);
                    log("cmp", `euconsent ${euconsent}`);
                    mark("cmp-got-consent");
                    setTimeout(invokeCallbacks, 0);
                },
                onMessageReady: (message_type) => {
                    log("cmp", `onMessageReady ${message_type}`);
                    if (message_type != frameworkMessageType) {
                        return;
                    }
                    mark("cmp-ui-displayed");
                },
                onMessageReceiveData: (message_type, data) => {
                    log("cmp", `onMessageReceiveData ${message_type}`);
                    if (message_type != frameworkMessageType) {
                        return;
                    }
                    if (data.messageId !== 0) {
                        messageId = data.messageId;
                        sendMessageReadyToOphan(constructBannerMessageId(messageId.toString()));
                    }
                    log("cmp", "onMessageReceiveData ", data);
                    void resolveWillShowPrivacyMessage(data.messageId !== 0);
                },
                onMessageChoiceSelect: (message_type, choice_id, choiceTypeID) => {
                    log("cmp", `onMessageChoiceSelect message_type: ${message_type}`);
                    if (message_type != frameworkMessageType) {
                        return;
                    }
                    log("cmp", `onMessageChoiceSelect choice_id: ${choice_id}`);
                    log("cmp", `onMessageChoiceSelect choice_type_id: ${choiceTypeID}`);
                    sendConsentChoicesToOphan(choiceTypeID, constructBannerMessageId(messageId.toString()));
                    if (choiceTypeID === SourcePointChoiceTypes.AcceptAll || choiceTypeID === SourcePointChoiceTypes.RejectAll || choiceTypeID === SourcePointChoiceTypes.Dismiss) {
                        setTimeout(invokeCallbacks, 0);
                        if (choiceTypeID === SourcePointChoiceTypes.RejectAll && message_type === "gdpr" && isConsentOrPayCountry(countryCode) && !useNonAdvertisedList) {
                            window.location.href = getSupportSignUpPage();
                        }
                    }
                },
                onPrivacyManagerAction: function (message_type, pmData) {
                    log("cmp", `onPrivacyManagerAction message_type: ${message_type}`);
                    if (message_type != frameworkMessageType) {
                        return;
                    }
                    log("cmp", `onPrivacyManagerAction ${pmData}`);
                },
                onMessageChoiceError: function (message_type, err) {
                    log("cmp", `onMessageChoiceError ${message_type}`);
                    if (message_type != frameworkMessageType) {
                        return;
                    }
                    log("cmp", `onMessageChoiceError ${err}`);
                },
                onPMCancel: function (message_type) {
                    log("cmp", `onPMCancel ${message_type}`);
                    if (message_type != frameworkMessageType) {
                        return;
                    }
                },
                onSPPMObjectReady: function () {
                    log("cmp", "onSPPMObjectReady");
                },
                onError: function (message_type, errorCode, errorObject, userReset) {
                    log("cmp", `errorCode: ${message_type}`);
                    if (message_type != frameworkMessageType) {
                        return;
                    }
                    log("cmp", `errorCode: ${errorCode}`);
                    log("cmp", errorObject);
                    log("cmp", `userReset: ${userReset}`);
                }
            }
        }
    };
    if (isInPropertyIdABTest) {
        window._sp_.config.propertyId = getPropertyId(framework, useNonAdvertisedList);
    }
    switch (framework) {
        case "tcfv2":
            window._sp_.config.gdpr = {
                targetingParams: {
                    framework,
                    excludePage: isExcludedFromCMP(pageSection),
                    isCorP: isConsentOrPayCountry(countryCode),
                    isUserSignedIn
                }
            };
            break;
        case "usnat":
            window._sp_.config.usnat = {
                targetingParams: {
                    framework
                },
                includeUspApi: true,
                transitionCCPAAuth: true
            };
            break;
        case "aus":
            window._sp_.config.ccpa = {
                targetingParams: {
                    framework
                }
            };
            break;
    }
    const spLib = document.createElement("script");
    spLib.id = "sourcepoint-lib";
    spLib.addEventListener("load", () => {
        if (shouldMergeVendorList(countryCode, useNonAdvertisedList)) {
            mergeVendorList().then(() => {
                window._sp_?.executeMessaging?.();
            }).catch((error) => {
                log("cmp", `'Failed to merge vendor list': ${error}`);
            });
        }
        else {
            window._sp_?.executeMessaging?.();
        }
    });
    spLib.src = `${ENDPOINT}/unified/wrapperMessagingWithoutDetection.js`;
    document.body.appendChild(spLib);
};
export { init, willShowPrivacyMessage };
